import React, { useState, useEffect } from 'react';
import { 
  Table, Tag, Avatar, Tooltip, Badge, Card, Row, Col, Typography, 
  Space, Input, Select, Button, Empty, Spin, Collapse, List, Divider,
  Tabs, Statistic
} from 'antd';
import { 
  CheckCircleOutlined, CloseCircleOutlined, UserOutlined, 
  SearchOutlined, ReloadOutlined, FilterOutlined, TeamOutlined,
  WalletOutlined, ShopOutlined, PercentageOutlined, MailOutlined,
  PhoneOutlined, GlobalOutlined, CalendarOutlined, DownOutlined,
  RightOutlined, CaretRightOutlined, ArrowDownOutlined, ArrowUpOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';
import { useLocation } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const UserPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const highlightUserId = queryParams.get('uid');

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubscription, setFilterSubscription] = useState('all');
  const [expandedUser, setExpandedUser] = useState(highlightUserId || null);

  // Metrics
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    verifiedUsers: 0,
    totalCoins: 0,
    growthRate: 0
  });

  // Chart states
  const [usersBySubscriptionData, setUsersBySubscriptionData] = useState({
    labels: ['Active Subscription', 'No Subscription'],
    series: [0, 0]
  });

  const [coinsDistributionData, setCoinsDistributionData] = useState({
    categories: ['0-100', '101-500', '501-1000', '1001+'],
    series: [{
      name: 'Users',
      data: [0, 0, 0, 0]
    }]
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (users.length > 0) {
      calculateMetrics();
      prepareChartData();
      applyFilters();
    }
  }, [users, searchText, filterStatus, filterSubscription]);

  // Add a utility function for retrying API calls
  const fetchWithRetry = async (url, options = {}, retries = 2, delay = 1000) => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (retries <= 0) {
        throw error;
      }
      console.log(`Retrying fetch to ${url}. Attempts remaining: ${retries}`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/fetchUserInfoAdmin', { 
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Check if data has a nested data array structure
      let usersArray;
      if (data.success && Array.isArray(data.data)) {
        usersArray = data.data;
      } else if (Array.isArray(data)) {
        usersArray = data;
      } else {
        console.error('Data is not in expected format:', data);
        setUsers([]);
        setFilteredUsers([]);
        return;
      }
      
      const processedData = usersArray.map(item => ({
        ...item,
        key: item.id,
        // Add a fake growth metric for demo
        growth_rate: Math.floor(Math.random() * 21) - 10 // -10 to +10
      }));
      setUsers(processedData);
      setFilteredUsers(processedData);
    } catch (error) {
      console.error('Fetch error:', error);
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    const totalUsers = users.length;
    const activeSubscriptions = users.filter(user => user.subscription_active).length;
    const verifiedUsers = users.filter(user => user.verified).length;
    const totalCoins = users.reduce((sum, user) => sum + (user.user_coins || 0), 0);
    
    // Calculate average growth rate for all users
    const sumGrowthRate = users.reduce((sum, user) => sum + (user.growth_rate || 0), 0);
    const growthRate = totalUsers > 0 ? (sumGrowthRate / totalUsers).toFixed(1) : 0;

    setMetrics({
      totalUsers,
      activeSubscriptions,
      verifiedUsers,
      totalCoins,
      growthRate
    });
  };

  const prepareChartData = () => {
    // Users by subscription status
    const activeSubscriptions = users.filter(user => user.subscription_active).length;
    const noSubscription = users.length - activeSubscriptions;
    
    setUsersBySubscriptionData({
      labels: ['Active Subscription', 'No Subscription'],
      series: [activeSubscriptions, noSubscription]
    });

    // Coins distribution
    const coinRanges = {
      '0-100': 0,
      '101-500': 0,
      '501-1000': 0,
      '1001+': 0
    };

    users.forEach(user => {
      const coins = user.user_coins || 0;
      if (coins <= 100) {
        coinRanges['0-100']++;
      } else if (coins <= 500) {
        coinRanges['101-500']++;
      } else if (coins <= 1000) {
        coinRanges['501-1000']++;
      } else {
        coinRanges['1001+']++;
      }
    });

    setCoinsDistributionData({
      categories: Object.keys(coinRanges),
      series: [{
        name: 'Users',
        data: Object.values(coinRanges)
      }]
    });
  };

  const applyFilters = () => {
    let result = [...users];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(user => 
        (user.name && user.name.toLowerCase().includes(lowerSearchText)) ||
        (user.email && user.email.toLowerCase().includes(lowerSearchText)) ||
        (user.uid && user.uid.toLowerCase().includes(lowerSearchText))
      );
    }
    
    // Apply subscription filter
    if (filterSubscription !== 'all') {
      const isActive = filterSubscription === 'active';
      result = result.filter(user => user.subscription_active === isActive);
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      const isVerified = filterStatus === 'verified';
      result = result.filter(user => user.verified === isVerified);
    }
    
    setFilteredUsers(result);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value);
  };

  const handleSubscriptionFilterChange = (value) => {
    setFilterSubscription(value);
  };

  const resetFilters = () => {
    setSearchText('');
    setFilterStatus('all');
    setFilterSubscription('all');
  };
  
  const toggleUserExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  // Chart options
  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom'
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--success-color)', 'var(--text-tertiary)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const barOptions = {
    chart: {
      type: 'bar',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '60%'
      },
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)'],
    yaxis: {
      title: {
        text: 'Number of Users'
      }
    },
    fill: {
      opacity: 1,
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.85,
        stops: [50, 100],
      },
    },
  };

  const renderUserDetails = (user) => {
    const hasSubscriptionPlan = user.subscription_active && user.user_plan;
    
    return (
      <div className="user-details-container">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <Card className="user-profile-card">
              <div className="user-profile-header">
                <Avatar 
                  src={user.dp_url} 
                  icon={!user.dp_url && <UserOutlined />}
                  size={80}
                  style={{ backgroundColor: 'var(--primary-color)' }}
                />
                <div className="user-profile-info">
                  <Title level={4}>{user.name || 'Unknown User'}</Title>
                  <div className="user-badges">
                    {user.verified && (
                      <Tag color="blue" icon={<CheckCircleOutlined />}>Verified</Tag>
                    )}
                    {user.seller && (
                      <Tag color="purple">Seller</Tag>
                    )}
                    {hasSubscriptionPlan && (
                      <Tag color="green">{user.user_plan}</Tag>
                    )}
                  </div>
                </div>
              </div>
              
              <Divider />
              
              <div className="user-profile-details">
                <div className="detail-item">
                  <MailOutlined className="detail-icon" />
                  <span>{user.email || 'No email'}</span>
                </div>
                {user.phone && (
                  <div className="detail-item">
                    <PhoneOutlined className="detail-icon" />
                    <span>{user.phone}</span>
                  </div>
                )}
                <div className="detail-item">
                  <GlobalOutlined className="detail-icon" />
                  <span>{user.preferred_language || 'English'}</span>
                </div>
                {user.last_login && (
                  <div className="detail-item">
                    <CalendarOutlined className="detail-icon" />
                    <span>Last login: {new Date(user.last_login).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </Card>
          </Col>
          
          <Col xs={24} md={16}>
            <Tabs defaultActiveKey="stats" className="user-details-tabs">
              <TabPane 
                tab={<span><BarChartOutlined /> Statistics</span>}
                key="stats"
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Card className="stat-mini-card">
                      <Statistic 
                        title="Coins Balance" 
                        value={user.user_coins || 0}
                        valueStyle={{ color: 'var(--primary-color)' }}
                        prefix={<WalletOutlined />}
                        suffix="coins"
                      />
                      {user.coins_expiry && (
                        <div className="stat-footnote">
                          Expires: {new Date(user.coins_expiry).toLocaleDateString()}
                        </div>
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card className="stat-mini-card">
                      <Statistic 
                        title="Subscription Status" 
                        value={user.subscription_active ? "Active" : "Inactive"} 
                        valueStyle={{ color: user.subscription_active ? 'var(--success-color)' : 'var(--danger-color)' }}
                        prefix={user.subscription_active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                      />
                      {user.plan_valid_till && (
                        <div className="stat-footnote">
                          Valid till: {new Date(user.plan_valid_till).toLocaleDateString()}
                        </div>
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card className="stat-mini-card">
                      <Statistic 
                        title="Referral Code" 
                        value={user.referral_code || 'N/A'} 
                        valueStyle={{ color: 'var(--text-primary)' }}
                      />
                      <div className="stat-footnote">
                        Invited: {user.invited_members?.length || 0} members
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card className="stat-mini-card">
                      <Statistic 
                        title="User Growth" 
                        value={user.growth_rate > 0 ? `+${user.growth_rate}%` : `${user.growth_rate}%`} 
                        valueStyle={{ 
                          color: user.growth_rate > 0 ? 'var(--success-color)' : (user.growth_rate < 0 ? 'var(--danger-color)' : 'var(--text-primary)')
                        }}
                        prefix={user.growth_rate > 0 ? <ArrowUpOutlined /> : (user.growth_rate < 0 ? <ArrowDownOutlined /> : null)}
                      />
                      <div className="stat-footnote">
                        Activity increase in last 30 days
                      </div>
                    </Card>
                  </Col>
                </Row>
              </TabPane>
              
              <TabPane 
                tab={<span><ShopOutlined /> Subscriptions</span>}
                key="subscriptions"
              >
                {(user.subscription_active && user.user_plan) ? (
                  <div className="subscription-info">
                    <Card className="subscription-card">
                      <div className="subscription-header">
                        <div>
                          <Tag color="green" className="subscription-tag">{user.user_plan}</Tag>
                          <div className="subscription-title">Active Subscription</div>
                        </div>
                        <Badge status="success" text="Active" />
                      </div>
                      
                      <Divider />
                      
                      <div className="subscription-details">
                        <div className="detail-row">
                          <span className="detail-label">Started On:</span>
                          <span className="detail-value">
                            {user.subscription_start_date ? 
                              new Date(user.subscription_start_date).toLocaleDateString() : 
                              'Unknown'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Valid Until:</span>
                          <span className="detail-value">
                            {user.plan_valid_till ? 
                              new Date(user.plan_valid_till).toLocaleDateString() : 
                              'Unknown'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Last Renewal:</span>
                          <span className="detail-value">
                            {user.last_renewal_date ? 
                              new Date(user.last_renewal_date).toLocaleDateString() : 
                              'Never'}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </div>
                ) : (
                  <Empty 
                    description="No active subscriptions" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </TabPane>
              
              <TabPane 
                tab={<span><WalletOutlined /> Transactions</span>}
                key="transactions"
              >
                <Empty 
                  description="Transaction history will appear here" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      </div>
    );
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 240,
      render: (text, record) => (
        <div 
          className="user-cell" 
          onClick={() => toggleUserExpand(record.id)}
          style={{ cursor: 'pointer' }}
        >
          <Avatar 
            src={record.dp_url} 
            icon={<UserOutlined />}
            style={{ backgroundColor: 'var(--primary-color)' }}
          />
          <div className="user-cell-info">
            <div className="user-cell-name">{text || 'Unknown'}</div>
            <div className="user-cell-email">{record.email || 'No email'}</div>
            <div className="user-cell-id">ID: {record.id}</div>
          </div>
          {expandedUser === record.id ? 
            <DownOutlined className="user-cell-arrow" /> : 
            <RightOutlined className="user-cell-arrow" />
          }
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <div className="user-status">
          <Tooltip title={record.verified ? "Verified User" : "Unverified User"}>
            <div className="status-item">
              <Badge status={record.verified ? "success" : "default"} />
              <span>{record.verified ? "Verified" : "Unverified"}</span>
            </div>
          </Tooltip>
          {record.seller && (
            <Tooltip title="Seller Account">
              <div className="status-item">
                <Badge status="processing" />
                <span>Seller</span>
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Subscription',
      key: 'subscription',
      width: 150,
      render: (_, record) => (
        <div>
          <Tag color={record.subscription_active ? "green" : "red"}>
            {record.subscription_active ? "Active" : "Inactive"}
          </Tag>
          {record.user_plan && (
            <div className="subscription-plan">{record.user_plan}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Coins',
      dataIndex: 'user_coins',
      key: 'user_coins',
      width: 120,
      render: (coins, record) => (
        <div className="user-coins">
          <div className="coins-value">
            <WalletOutlined style={{ marginRight: '4px' }} />
            {coins || 0}
          </div>
          {record.coins_expiry && (
            <div className="coins-expiry">
              Expires: {new Date(record.coins_expiry).toLocaleDateString()}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Referrals',
      key: 'referrals',
      width: 150,
      render: (_, record) => (
        <div className="user-referrals">
          <div className="referral-code">{record.referral_code || 'No code'}</div>
          <div className="invited-count">
            {record.invited_members?.length || 0} invited
          </div>
        </div>
      ),
    },
    {
      title: 'Activity',
      key: 'activity',
      width: 150,
      render: (_, record) => {
        const growthRate = record.growth_rate || 0;
        return (
          <div className="user-activity">
            <div 
              className="growth-indicator" 
              style={{ 
                color: growthRate > 0 ? 'var(--success-color)' : (growthRate < 0 ? 'var(--danger-color)' : 'var(--text-tertiary)')
              }}
            >
              {growthRate > 0 ? <ArrowUpOutlined /> : (growthRate < 0 ? <ArrowDownOutlined /> : null)}
              <span>{growthRate > 0 ? `+${growthRate}%` : `${growthRate}%`}</span>
            </div>
            {record.last_login && (
              <div className="last-login">
                Last login: {new Date(record.last_login).toLocaleDateString()}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  if (loading && users.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading user data..." spinning={true} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <TeamOutlined /> User Management
        </Title>
        <Text type="secondary">
          Manage and view information about all registered users
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalUsers}</div>
              <div className="stat-label">Total Users</div>
            </div>
            <TeamOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card success">
            <div>
              <div className="stat-value">{metrics.activeSubscriptions}</div>
              <div className="stat-label">Active Subscriptions</div>
            </div>
            <ShopOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card accent">
            <div>
              <div className="stat-value">{metrics.verifiedUsers}</div>
              <div className="stat-label">Verified Users</div>
            </div>
            <CheckCircleOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card secondary">
            <div>
              <div className="stat-value">{metrics.totalCoins.toLocaleString()}</div>
              <div className="stat-label">Total Coins</div>
            </div>
            <WalletOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Subscription Status Distribution" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {usersBySubscriptionData.series.some(value => value > 0) ? (
              <Chart
                options={{
                  ...donutOptions,
                  labels: usersBySubscriptionData.labels
                }}
                series={usersBySubscriptionData.series}
                type="donut"
                height="280"
              />
            ) : (
              <Empty description="No user data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Coins Distribution" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {coinsDistributionData.series[0].data.some(value => value > 0) ? (
              <Chart
                options={{
                  ...barOptions,
                  xaxis: {
                    categories: coinsDistributionData.categories
                  }
                }}
                series={coinsDistributionData.series}
                type="bar"
                height="280"
              />
            ) : (
              <Empty description="No coins data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Input
              placeholder="Search by name, email or ID"
              value={searchText}
              onChange={handleSearch}
              style={{ width: '250px' }}
              prefix={<SearchOutlined />}
              allowClear
            />
            
            <Select
              value={filterStatus}
              onChange={handleStatusFilterChange}
              style={{ width: '150px' }}
              placeholder="Filter by status"
            >
              <Option value="all">All Status</Option>
              <Option value="verified">Verified</Option>
              <Option value="unverified">Unverified</Option>
            </Select>
            
            <Select
              value={filterSubscription}
              onChange={handleSubscriptionFilterChange}
              style={{ width: '180px' }}
              placeholder="Filter by subscription"
            >
              <Option value="all">All Subscriptions</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={resetFilters}
              type="text"
              title="Reset filters"
            >
              Reset
            </Button>
          </div>
          
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
        </div>
        
        <div className="user-table-container">
          {filteredUsers.map(user => (
            <div key={user.id} className="user-row-container">
              <div 
                className={`user-row ${expandedUser === user.id ? 'expanded' : ''}`}
                onClick={() => toggleUserExpand(user.id)}
              >
                <Row gutter={16} align="middle" style={{ width: '100%' }}>
                  <Col flex="auto" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Avatar 
                      src={user.dp_url} 
                      icon={<UserOutlined />}
                      style={{ backgroundColor: 'var(--primary-color)' }}
                    />
                    <div>
                      <div className="user-name">{user.name || 'Unknown'}</div>
                      <div className="user-email">{user.email || 'No email'}</div>
                    </div>
                  </Col>
                  <Col flex="0 0 120px">
                    <Badge 
                      status={user.verified ? "success" : "default"} 
                      text={user.verified ? "Verified" : "Unverified"} 
                    />
                  </Col>
                  <Col flex="0 0 130px">
                    <Tag color={user.subscription_active ? "green" : "red"}>
                      {user.subscription_active ? "Subscribed" : "Not Subscribed"}
                    </Tag>
                  </Col>
                  <Col flex="0 0 100px">
                    <div className="user-coins">
                      <WalletOutlined style={{ marginRight: '4px' }} />
                      {user.user_coins || 0}
                    </div>
                  </Col>
                  <Col flex="0 0 40px" style={{ textAlign: 'right' }}>
                    {expandedUser === user.id ? 
                      <DownOutlined className="expand-icon" /> : 
                      <RightOutlined className="expand-icon" />
                    }
                  </Col>
                </Row>
              </div>
              
              {expandedUser === user.id && (
                <div className="user-expanded-content">
                  {renderUserDetails(user)}
                </div>
              )}
            </div>
          ))}
          
          {filteredUsers.length === 0 && (
            <Empty description="No users match your filters" />
          )}
        </div>
      </Card>
    </div>
  );
};

export default UserPage;
